// src/components/CameraCapture.js
import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import api from "../api";
import Form from "react-bootstrap/Form";

import {
  isMobile,
  isAndroid,
  isIOS,
  isMacOs,
  isWindows,
} from "react-device-detect";

// Import FontAwesome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// TODO: add retry icon and hide the upload panel on match
import {
  faCamera,
  faUpload,
  faSearch,
  faTrash,
  faRecycle
} from "@fortawesome/free-solid-svg-icons";

import AnimatedCameraButton from "./AnimatedCameraButton";
import { Button, Container, Row, Image, Spinner } from "react-bootstrap";

const CameraCapture = ({
  event_id,
  setMatchGallery,
  setShowMatch,
  setMatchInfo
}) => {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [tolerance, setTolerance] = useState(0.53); // Default tolerance value
  const [precision, setPrecision] = useState(0.47); // Default precision value
  const [isCameraOpen, setIsCameraOpen] = useState(false); // State to control camera visibility
  const [isUploading, setIsUploading] = useState(false);

  const showPrecision = false; // Set to true to show the precision slider
  
  const videoConstraints = {
    width: { ideal: 1260 }, // Set ideal width
    height: { ideal: 720 }, // Set ideal height
    facingMode: "user", // Use the front camera
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Programmatically trigger the file input
    }
  };

  const handleShowMatchGallery = (images) => {
    if (images) {
      setMatchGallery(images);
      setShowMatch(true);
      setImage(null);
    } else {
      setMatchGallery([]);
      setShowMatch(false);
    }
  };

  // Open the camera when 'Scan' button is clicked
  const openCamera = () => {
    setIsCameraOpen(true);
    resetCapture();
  };

  // Open the camera when 'Scan' button is clicked
  const resetCapture = () => {
    // Reset the image state
    setImage(null);
    // Reset the match state
    handleShowMatchGallery(null);
    fileInputRef.current.value = null;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImage(reader.result); // Display the selected image
      reader.readAsDataURL(file);
      setShowMatch(false);
    }
  };

  const closeCamera = () => {
    setIsCameraOpen(false);
  };

  // Capture the image
  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setImage(imageSrc);
    setShowMatch(false);
    closeCamera();
  };

  // Upload the image to the ML endpoint
  const getImageMatch = async () => {
    setIsUploading(true);

    const mimeType = image.match(/^data:(image\/[a-zA-Z]+);base64,/)[1];
    // console.log(mimeType); // e.g., "image/png"

    const extension = mimeType.split("/")[1]; // "png", "jpeg", etc.
    const imageName = `image_${Date.now()}.${extension}`;

    const blob = await fetch(image).then((res) => res.blob());
    handleShowMatchGallery(null);

    // Create FormData for the image file
    const formData = new FormData();
    formData.append("picture", blob, imageName); // Append the image blob with its name

    try {
      /** retrieve the event id from http://localhost:3000/1ASDF3 */

        const response = await api.post(
        `/get_match?eid=${event_id}&tolerance=${tolerance}`,
        formData,
        {
          timeout: 10000,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      // console.log('Response:', response.data);
      // alert('Image uploaded successfully!');
      handleShowMatchGallery(response.data.images);
      setMatchInfo(response.data.infos);
    } catch (error) {
      // {"error":"no directory found"}
      console.error("Error uploading image:", error);
      // show the error data when exist
      if (error.response && error.response.data) {
        alert(error.response.data.error);
      } else {
        alert("An error occured. Please try again later.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className={`text-center bg-light col-12 col-md-6 mx-auto p-3 shadow mt-4 ${
        isCameraOpen || image ? "rounded-5" : "rounded-pill"
      } 
      `}
    >
      {!image && !isCameraOpen && (
        <div className="">
          <div className="d-flex justify-content-center m-3 align-items-center">
            <AnimatedCameraButton
              onClick={openFilePicker}
              size={40}
              color="grey"
            />
          </div>
          {/* Bold */}
          <h4 className="comic-neue fw-bold" onClick={openFilePicker}>Click me</h4>
        </div>
      )}
      {
        // retry upload btn if the image is already captured
        image && (
          <div className="d-flex justify-content-center align-items-center">
            <Button
              variant="outline-secondary"
              onClick={resetCapture}
              className=""
            >
              {/* clear icon*/}
              <FontAwesomeIcon icon={faTrash} size="1x" />
            </Button>
          </div>
        )
      }
      <Container>
        {/* Show the camera when the "Scan" button is clicked */}
        {isCameraOpen && (
          <div>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width={420}
              height={240}
              videoConstraints={videoConstraints}
            />
            <div style={{ margin: "20px 0" }}>
              {/* Capture button with Camera Icon */}
              <Button onClick={capture} style={styles.cameraButton}>
                <FontAwesomeIcon icon={faCamera} size="3x" />
              </Button>
            </div>
          </div>
        )}
      </Container>

      <Container className="">
        <div className="m-1">
          {/* Scan button to open the camera */}
          {!isCameraOpen && (
            <>
              <div className="d-flex justify-content-center align-items-center">
                {!isMobile && !image && (
                  <>
                    <Button variant="outline-primary" onClick={openCamera}>
                      <FontAwesomeIcon icon={faCamera} size="3x" />
                    </Button>
                    <strong className="m-2">/</strong>
                    <Button onClick={openFilePicker} variant="outline-primary">
                      <FontAwesomeIcon icon={faUpload} size="3x" />
                    </Button>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }} // Keep it hidden
                  onChange={handleFileChange}
                  onClick={resetCapture}
                />
              </div>

              <div className="d-block mt-4">
                {!image && (
                  <>
                    <span className="comic-neue d-block text-secondary">
                      Take a selfie / upload a photo
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <div>
          {/* Show preview and the 'Show the magie' button */}
          {image && (
            <>
              <Image
                thumbnail
                src={image}
                alt="Captured"
                style={{ maxWidth: 250, maxHeight: 250 }}
              />
              <div className="m-2">
                {isUploading ? (
                  <>
                    {/* Boostrap spinner */}
                    <div className="m-1 text-secondary p-2 fs-6">
                      <span className="m-2">Looking for your photos</span>
                      <FontAwesomeIcon
                        icon={faSearch}
                        flip={true}
                        size="1x"
                        className="me-1"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <span className="d-block"></span>
                  {/* tolerance input type float */}
                  { showPrecision && (
                    
                    <Form.Group controlId="formPrecision" className="mb-3 d-flex flex-column align-items-center">
                      <Form.Label>Precision: {precision.toFixed(2)}</Form.Label>
                      <Form.Range
                        style={{ width: "50%" }}
                        min={0}
                        max={1}
                        step={0.01}
                        value={precision}
                        onChange={(e) => {
                          const p = parseFloat(e.target.value);
                          setPrecision(p);               // UI
                          setTolerance(1 - p);           // Backend logic
                        }}
                      />
                      <div className="d-flex justify-content-between w-50">
                      <Form.Text className="text-muted">
                        Tolerant
                      </Form.Text>
                      <Form.Text className="text-muted">
                        Strict
                      </Form.Text>
                      </div>
                    </Form.Group>
                  
                )}

                    <Button
                      className="m-1 shadow"
                      variant="outline-secondary"
                      onClick={getImageMatch}
                      disabled={isUploading}
                    >
                      <FontAwesomeIcon
                        icon={faSearch}
                        bounce={true}
                        size="1x"
                        className="me-1"
                      />
                      Search me
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

// Inline style for camera button (optional)
const styles = {
  cameraButton: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    padding: "10px",
    borderRadius: "50%",
    cursor: "pointer",
    outline: "none",
  },
};

export default CameraCapture;
